import {
  LOCAL_CONTACT,
  PROPERTY_MANAGEMENT_OPTIONS,
} from '../components/payment/constants';
import dayjs from 'dayjs';
import { constructMarkup as helperConstructMarkup } from '../components/common/CustomHTML';
import { Element } from 'html-react-parser';
import { LicensePeople } from '../types/licenses';
import {
  CollectionAgreementBucket,
  CollectionAgreementBucketWith,
} from '../types/tax';

// TODO Deprecate this function
export const isNullUndefinedOrEmptyObject = (value: unknown) =>
  isNullOrUndefined(value) || (value && Object.keys(value).length === 0);
// TODO Deprecate this function
export const isNullUndefinedOrEmptyString = (value?: string) =>
  isNullOrUndefined(value) || value === '';
// TODO Deprecate this function
export const isNullOrUndefined = (value: unknown) =>
  value === null || value === undefined;

export const generate_uuidv4 = (useUnderscores = false): string => {
  const uuid = '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
    (
      +c ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))
    ).toString(16)
  );
  if (useUnderscores) return uuid.replace('-', '_');
  return uuid;
};

export const sanitizeAndParseConfigLink = (
  domNode: Element
): {
  href: string;
  referrer: string;
  target: string;
} => {
  return {
    href: stripBackslashesAndQuotes(domNode?.attribs?.href) || '',
    referrer: stripBackslashesAndQuotes(domNode?.attribs?.referrer) || '',
    target: stripBackslashesAndQuotes(domNode?.attribs?.target) || '',
  };
};

export const stripBackslashesAndQuotes = (text: string): string | null => {
  if (isNullUndefinedOrEmptyObject(text) || isNullUndefinedOrEmptyString(text))
    return null;
  const backslashesStripped = text
    .split('\\')
    .filter((r) => !isNullUndefinedOrEmptyString(r))[0];
  const quotesStripped = backslashesStripped
    .split('"')
    .filter((r) => !isNullUndefinedOrEmptyString(r))[0];
  return !isNullUndefinedOrEmptyString(quotesStripped) ? quotesStripped : null;
};

// Use this instead of hasOwnProperty to avoid linter errors
// TODO Candidate to deprecate
export const objectHasProperty = (
  propertyName: string,
  obj: Record<string, unknown>
) => propertyName in obj;

// Converts the input to a string with no white space and only alphanumeric characters
// If converted string is empty, returns an underscore separated uuid instead
export const convertStringToKey = (input: string): string => {
  if (!input) return generate_uuidv4(true);
  const convertedKey = input
    .replace(/[^a-zA-Z0-9_\s]/g, '')
    .replace(/\s/g, '_')
    .toLowerCase();
  if (isNullUndefinedOrEmptyString(convertedKey)) return generate_uuidv4(true);
  return convertedKey;
};

export const unescapeConfigString = (escapedText: string): string => {
  return escapedText.split('\\').join('');
};

export const roundDecimalPlaces = (
  value: number,
  decimalPlaces: number = 2
) => {
  if (isNaN(value)) return undefined;

  return parseFloat(value.toFixed(decimalPlaces));
};

export const convertNumberToCurrency = (number?: string | number): string => {
  if (number === undefined) return '';
  if (typeof number === 'string') return number;
  if (!Number.isFinite(number)) return '';
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });
  return formatter.format(number);
};

export const convertStringToCurrency = (input: string): string => {
  const number = parseFloat(input);
  if (isNaN(number)) {
    // If parsing fails, return input as is
    return input;
  }
  return convertNumberToCurrency(number);
};

/** Capitalizes a text */
export const titleize = (string: string): string => {
  return string
    .toLowerCase()
    .replace(
      /([a-zA-Z0-9']+)/g,
      (word) => word.charAt(0).toUpperCase() + word.slice(1)
    );
};

export const areObjectsEqual = (objA: unknown, objB: unknown): boolean => {
  return JSON.stringify(objA) === JSON.stringify(objB);
};

export const getAddressWithUnitNumber = (
  address?: string,
  unitNumber?: string
): string => {
  const unitNumberString = !isNullUndefinedOrEmptyString(unitNumber)
    ? ` (Unit ${unitNumber})`
    : '';
  const fullAddress = `${address}${unitNumberString}`;
  return titleize(fullAddress);
};

/**
 * Smarty Streets' autocomplete search does not reliably match a bare "#"
 * secondary designator (e.g. addresses sourced from a backend APN/parcel
 * lookup, which are never validated through Smarty). Normalize it to "Unit"
 * and collapse extra whitespace before using the string as Smarty search input.
 */
export const normalizeAddressForSmartyLookup = (address?: string): string => {
  if (!address) {
    return '';
  }
  return address
    .replace(/#\s*(\S+)/g, 'Unit $1')
    .replace(/\s+/g, ' ')
    .trim();
};

export const beginsWithVowel = (text: string): boolean => {
  if (!text) return false;
  const firstLetter = text[0];
  const vowels = ['a', 'e', 'i', 'o', 'u'];
  return vowels.includes(firstLetter.toLowerCase());
};

const hashCode = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
};

export const objectHash = (obj: unknown): number => {
  const jsonString = JSON.stringify(obj);
  return hashCode(jsonString);
};

export const convertSnakeCaseToCamelCase = (text: string): string =>
  text.replace(/(_\w)/g, (k) => k[1].toUpperCase());

export const determineSelectedManagementOption = (
  licensePeople: LicensePeople,
  isAdminUpdate: boolean = false
): PROPERTY_MANAGEMENT_OPTIONS => {
  const ownersArray = Array.isArray(licensePeople.owner)
    ? licensePeople.owner
    : [licensePeople.owner]; // Pages expect this to be an array, but towns that don't allows multiple owners return this as a single object

  if (isAdminUpdate) {
    if (
      licensePeople.propertyManager &&
      !areObjectsEqual(ownersArray[0], licensePeople.propertyManager)
    ) {
      return PROPERTY_MANAGEMENT_OPTIONS.PROPERTY_MANAGER;
    }
  } else if (
    licensePeople.tenant &&
    licensePeople.tenant.email === licensePeople.propertyManager?.email
  ) {
    return PROPERTY_MANAGEMENT_OPTIONS.TENANT;
  } else if (
    licensePeople.propertyManager &&
    !ownersArray.some(
      (owner) => owner.email === licensePeople.propertyManager?.email
    )
  ) {
    // The Property Manager is pre-selected if there exists a Property Manager and it is
    // not an owner of the property
    return PROPERTY_MANAGEMENT_OPTIONS.PROPERTY_MANAGER;
  }
  return PROPERTY_MANAGEMENT_OPTIONS.SELF_MANAGED;
};

export const determineSelectedOwnerRepOption = (
  licensePeople: LicensePeople,
  isAdminUpdate: boolean = false
): LOCAL_CONTACT => {
  const ownersArray = Array.isArray(licensePeople.owner)
    ? licensePeople.owner
    : [licensePeople.owner]; // Pages expect this to be an array, but towns that don't allows multiple owners return this as a single object

  if (isAdminUpdate) {
    if (areObjectsEqual(licensePeople.localContact, ownersArray[0])) {
      return LOCAL_CONTACT.OWNER;
    } else if (
      areObjectsEqual(licensePeople.localContact, licensePeople.propertyManager)
    ) {
      return LOCAL_CONTACT.PROPERTY_MANAGER;
    }
  } else {
    const localContact = licensePeople.localContact?.email;
    if (ownersArray.some((owner) => owner.email === localContact)) {
      return LOCAL_CONTACT.OWNER;
    } else if (localContact === licensePeople.propertyManager?.email) {
      return LOCAL_CONTACT.PROPERTY_MANAGER;
    } else if (localContact === licensePeople.tenant?.email) {
      return LOCAL_CONTACT.TENANT;
    }
  }
  return LOCAL_CONTACT.OTHER;
};

// This is just an alias of the CustomHTML Component, which is present
// in several components. Not moved in this PR as would require several
// changes
export const constructMarkup = helperConstructMarkup;

export const parseArbitraryStringToHtmlElementId = (text: string): string => {
  if (text.length === 0) return text;
  return ('attr-' + text).split(' ').join('-').toLowerCase();
};

export const getMostRecentPastDate = (
  dates: CollectionAgreementBucketWith,
  comparisonDate: string
) => {
  // Get all past dates
  const pastDates = Object.keys(dates)
    .filter(
      (date) =>
        dayjs(date).isBefore(dayjs(comparisonDate, 'month')) ||
        dayjs(date).isSame(comparisonDate, 'month')
    )
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
  return pastDates.length > 0 ? pastDates[0] : null;
};

export const getDateRelatedBucket = (
  buckets: CollectionAgreementBucketWith,
  date: string
): Array<CollectionAgreementBucket> => {
  const mostRecentDate = getMostRecentPastDate(buckets, date);

  if (mostRecentDate) {
    return buckets[mostRecentDate].buckets;
  }
  return [];
};
